import { z } from 'zod';
/**
 * Demo-data seeding for `/api/v1` (KIR-4084).
 *
 * WWT resells Kirin by hosting a customer-limited lab environment per prospect
 * (KIR-4068). They provision one organization per demo tenant from a script,
 * and an empty organization is a bad demo — so this surface fills one with
 * data that looks like a working deployment.
 *
 * **This endpoint is deliberately absent from the published OpenAPI document.**
 * It fabricates data, which is the opposite of what every other `/api/v1`
 * endpoint does, and advertising it to every customer reading the reference
 * would invite exactly the wrong question. Hiding is presentational only: the
 * route sits behind the same access-key auth, the same org-scope hook and a
 * stricter role gate than any other write here, plus a feature flag that is off
 * by default. See `apps/backend/src/routes/v1/demo-data.ts`.
 *
 * Because it is unpublished, these shapes are **not** frozen the way the rest of
 * `contracts/v1` is — there is no customer integration to break. They are
 * defined here anyway so the route validates and serializes like its neighbours
 * rather than growing its own conventions.
 */
/**
 * What one phase of the seed accomplished.
 *
 * Two counters rather than a boolean because a phase is not all-or-nothing. The
 * seed drives real ingestion, and parts of that reach OSV, S3 and Kirin AI — any
 * of which can fail for one item while the rest of the phase succeeds. A caller
 * that is told only "the request failed" cannot tell an untouched organization
 * from a mostly-populated one, and the retry it then attempts is additive.
 */
export const v1DemoDataPhaseResultSchema = z
    .object({
    seeded: z
        .number()
        .int()
        .min(0)
        .describe('How many items this phase created or updated.'),
    failed: z
        .number()
        .int()
        .min(0)
        .describe('How many items this phase attempted and could not complete. Non-zero does not mean the phase did nothing — read it alongside `seeded`.'),
})
    .meta({
    id: 'V1DemoDataPhaseResult',
    title: 'Demo-data phase result',
    description: 'The outcome of one phase of a demo-data seed.',
});
/**
 * The seed's per-phase report.
 *
 * Two keys are worth reading twice. `projects` reports what a read-back saw
 * rather than what the seed wrote, because a project is a by-product of
 * ingesting a file that carries git context and cannot be written directly.
 * And there is no `auditLogs` key at all, which is a deliberate absence rather
 * than an oversight. Every table the ingest path writes is excluded from the audit
 * trigger — `findings` outright, `findings_groups` on INSERT, and every
 * `shadow_ai_%` table by pattern — because scan volume would otherwise drown the
 * audit trail. Audit rows for a seed therefore come from its *administrative*
 * steps (creating users and the api key, setting policy modes, muting alerts),
 * which the trigger records as genuine events. Nothing about the audit trail is
 * fabricated, so there is nothing here to count.
 */
export const v1DemoDataSeedResultSchema = z
    .object({
    users: v1DemoDataPhaseResultSchema.describe('Demo developers provisioned in Descope and mirrored into the local user table. These are the identities every other phase attributes its rows to.'),
    securityGroups: v1DemoDataPhaseResultSchema.describe('Security groups created, plus each developer added to one. Counted together because they are one phase: a group nobody belongs to changes no screen. Seeded before alerts, because an alert records the group of whoever reported it at ingest and never recomputes it. Skipped, reporting zero, when the organization does not have the security-groups feature - with that flag off the groups are invisible AND the alerts carry no group, so writing them would report rows over two empty surfaces.'),
    policies: v1DemoDataPhaseResultSchema.describe('Policy modes and allow/block entries set on the organization. Required rather than cosmetic: several detections default to Off, and two only fire against a block-list entry, so without this phase the alerts phase produces nothing.'),
    alerts: v1DemoDataPhaseResultSchema.describe('Findings produced by replaying real scans through the same ingestion the IDE extension uses. Counts findings, not rows on the Alerts screen - one fixture replayed by several developers groups into a single alert with several occurrences. Alerts are spaced a second or two apart but all land on the day of the call; see the endpoint description.'),
    shadowAi: v1DemoDataPhaseResultSchema.describe('Shadow-AI findings: installed AI tools, AI tools seen in browser history, running processes and observed AI network destinations. Counts only developers that are Descope tenant members, because every Shadow AI screen filters on tenant membership at read time - rows written for a non-member render nowhere, so they are reported as failed rather than seeded.'),
    mcpServers: v1DemoDataPhaseResultSchema.describe('MCP servers written to the inventory. Counts developers whose inventory write completed, and reports the whole phase as failed if reading the rows back shows fewer distinct servers than the fixture defines.'),
    extensions: v1DemoDataPhaseResultSchema.describe('IDE extensions written to the inventory, for the developers whose platform has an extension surface.'),
    plugins: v1DemoDataPhaseResultSchema.describe('Agent-CLI plugins written to the inventory, for the developers on an agent CLI. A separate table and a separate tab from extensions.'),
    aiInstructions: v1DemoDataPhaseResultSchema.describe('Benign AI instruction files (rules, CLAUDE.md, Copilot instructions). The malicious ones are counted under `alerts`, because being flagged is what they are for.'),
    hooks: v1DemoDataPhaseResultSchema.describe('Agent and git hooks written to the inventory.'),
    projects: v1DemoDataPhaseResultSchema.describe('Projects VISIBLE after seeding, read back rather than written. Nothing reports a project: one is resolved from the git context on an ingested file, and a project owning no files is hidden as an empty husk. `failed` is the shortfall against the repositories the fixtures name.'),
    agentCommands: v1DemoDataPhaseResultSchema.describe('Package-install and git commands behind Agents Activity / Packages & Repos. Skipped, reporting zero, when the organization does not have the agent-command-visibility feature.'),
    agentActivity: v1DemoDataPhaseResultSchema.describe('Per-developer client activity, which drives the coverage indicators on Inventory and the dashboard.'),
})
    .meta({
    id: 'V1DemoDataSeedResult',
    title: 'Demo-data seed result',
    description: 'Per-phase counts for one demo-data seed. Phases run independently, so a failure in one does not stop the others.',
});
//# sourceMappingURL=demo-data.js.map